import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { useGoBack } from "@/hooks/useGoBack";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useNavigationGuardStore } from "@/store/navigationGuardStore";

type BackLinkBase = {
  fallback?: string;
  className?: string;
};

type BackLinkProps = BackLinkBase &
  (
    | {
        variant?: "text" | "ghost" | "outline";
        children: ReactNode;
        "aria-label"?: string;
      }
    | {
        variant: "icon";
        children?: undefined;
        "aria-label": string;
      }
  );

export type { BackLinkProps };

export function BackLink(props: BackLinkProps) {
  const { fallback = "/", className, variant = "text" } = props;
  const goBack = useGoBack(fallback);
  const isBlocking = useNavigationGuardStore((s) => s.isBlocking);
  const openUnsavedPrompt = useNavigationGuardStore((s) => s.openUnsavedPrompt);
  const ariaLabel = props["aria-label"];

  const handleBack = () => {
    if (isBlocking) {
      openUnsavedPrompt();
      return;
    }
    goBack();
  };

  if (variant === "icon") {
    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleBack}
        className={className}
        aria-label={ariaLabel}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
    );
  }

  if (variant === "ghost" || variant === "outline") {
    return (
      <Button
        type="button"
        variant={variant}
        size="sm"
        onClick={handleBack}
        className={className}
        aria-label={ariaLabel}
        leftIcon={<ArrowLeft className="h-4 w-4" />}
      >
        {props.children}
      </Button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleBack}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-2 text-sm font-medium text-surface-600 hover:text-surface-900",
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" />
      {props.children}
    </button>
  );
}
